import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CmpsDialog } from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import { startWith, take, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { AuthService } from '../auth.service';
import { ResetAuthComponent } from '../reset.auth.component';

import { TranslationKeyMap } from './login.auth.i18n';
import { Step } from './step.model';

@Component({
    selector: 'cmps-auth-login',
    templateUrl: './login.auth.component.html',
    host: {
        class: 'cmps-auth-login',
    },
})
@TakeUntilDestroy()
export class LoginAuthComponent
    implements OnInit, AfterViewInit, OnDestroy, TakeUntilDestroy
{
    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _Step = Step;

    @Input() isReAuth: boolean;

    /** @private */
    _authorizationCodeGrantType: boolean;

    /** @private */
    _tokenFragment: boolean;

    /** @private */
    _formGroup: FormGroup;

    /** @private */
    _noticeText: string;

    /** @private */
    _step: Step;

    /** @private */
    _serverError: string;

    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _router: Router,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _ngZone: NgZone,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        this._step = Step.AUTH_METHODS;
    }

    ngOnInit(): void {
        this._formGroup = this._formBuilder.group({
            username: [null, [Validators.required]],
            password: [null, [Validators.required]],
            code: [null],
            redirectUri: [this._appConfig.OAUTH.REDIRECT_URI],
            languageId: [null],
        });

        this._checkAuthorizationCodeGrantType();
        this._checkTokenFragment();

        if (this.isReAuth) {
            this._formGroup.patchValue({
                username: this._authService.user?.securityInfo?.username,
            });
            this._formGroup.get('username').disable();
        }

        this._setupNoticeText();
    }

    ngAfterViewInit(): void {
        /* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
        (
            this._el?.nativeElement?.querySelector(
                'input[formControlName="username"]',
            ) as HTMLElement
        )?.focus();
        /* eslint-enable @typescript-eslint/no-unnecessary-type-assertion */
    }

    ngOnDestroy(): void {
        //
    }

    /** @private */
    _onDetermineAuthMethodsClick(): void {
        this._serverError = null;
        this._formGroup.disable();
        this._authService
            .determineAuthenticationMethods(this._formGroup.value?.username)
            .pipe(
                tap((authMethods) => {
                    if (
                        authMethods?.length > 0 &&
                        [Step.AUTH_PWD, Step.AUTH_SAML2].includes(
                            authMethods[0] as Step,
                        )
                    ) {
                        this._step = authMethods[0] as Step;
                        let selector: string | null = null;
                        if (this._step === Step.AUTH_PWD) {
                            selector = 'input[formControlName="password"]';
                        }
                        if (this._step === Step.AUTH_SAML2) {
                            selector = 'button[name="saml2"]';
                        }
                        this._ngZone.onStable
                            .pipe(
                                take(1),
                                tap(() => {
                                    /* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
                                    (
                                        this._el?.nativeElement?.querySelector(
                                            selector,
                                        ) as HTMLElement
                                    )?.focus();
                                    /* eslint-enable @typescript-eslint/no-unnecessary-type-assertion */
                                }),
                                takeUntilDestroy(this),
                            )
                            .subscribe();
                    } else {
                        this._serverError =
                            TranslationKeyMap._704_UNKNOWN_ERROR;
                    }
                    this._formGroup.enable();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onForgotPwdClick(): void {
        this._dialog.open(ResetAuthComponent);
    }

    /** @private */
    _onPwdAuthClick(): void {
        this._serverError = null;
        this._formGroup.disable();
        this._authService
            .login(this._formGroup.value)
            .pipe(takeUntilDestroy(this))
            .subscribe({
                error: (err) => {
                    this._serverError =
                        err?.message || TranslationKeyMap._704_UNKNOWN_ERROR;
                    this._formGroup.enable();
                },
            });
    }

    /** @private */
    _onSaml2AuthClick(): void {
        window.location.href = `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/saml2/login`;
    }

    private _checkAuthorizationCodeGrantType(): void {
        if (window.location.href?.includes('code=')) {
            this._authorizationCodeGrantType = true;
            const params = new URL(window.location.href).searchParams;
            this._formGroup.patchValue({ code: params.get('code') });
            this._authService
                .loginByAuthorizationCode(this._formGroup.value)
                .pipe(takeUntilDestroy(this))
                .subscribe({
                    error: () => {
                        void this._router.navigate(['logout']);
                    },
                });
        }

        if (window.location.hash?.startsWith('#error')) {
            this._step = Step.AUTH_SAML2;
            this._serverError = TranslationKeyMap._704_UNKNOWN_ERROR;
        }
    }

    private _checkTokenFragment(): void {
        if (window.location.hash?.startsWith('#access_token')) {
            this._tokenFragment = true;
            this._authService
                .loginByTokenFragment(window.location.hash.substr(1))
                .pipe(takeUntilDestroy(this))
                .subscribe({
                    error: () => {
                        void this._router.navigate(['logout']);
                    },
                });
        }
    }

    private _setupNoticeText(): void {
        const key = TranslationKeyMap._7162_PLACEHOLDER_LOGIN_NOTICE;
        this._translateService.onLangChange
            .pipe(
                startWith({ lang: this._translateService.currentLang }),
                tap((evt: LangChangeEvent) => {
                    const ret = this._translateService.instant(key);
                    this._noticeText = (
                        ret != null && ret !== key ? ret : ''
                    ).trim();

                    this._formGroup.patchValue({
                        languageId: parseInt(evt.lang),
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
